import { useState } from 'react';
import { type AddPatientSetup } from '../../../lib';
import CareMapprButton from '../Button/CareMapprButton';
import AddPatientFormInfo from './AddPatientFormInfo';
import MostRecentEPOCC from './MostRecentEPOCC';
import { useForm, FormProvider } from 'react-hook-form';

import { useClinicianProfile } from '../../hooks';
import useAddTenancyPatient from './useAddTenancyPatient';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store';
import { useTriggerUpdate } from '../../../context';
import { transformToAddTenancyPatientPayload } from '../../../lib/payloads/addTenancyPatient';
import type { AddTenancyPatientPayload } from '../../../lib/payloads/addTenancyPatient';

interface AddTenancyPatientFormProps {
  features: AddPatientSetup;
  handleClose: () => void;
}

const AddTenancyPatientForm = ({
  features,
  handleClose,
}: AddTenancyPatientFormProps) => {
  const { selectedTenancy } = useClinicianProfile();
  const { triggerPatientListUpdate, resetHaveMoreData } = useTriggerUpdate();
  const methods = useForm({ defaultValues: {} });
  const { loading, addAndInvitePatient } = useAddTenancyPatient();
  const { clinics } = useSelector((state: RootState) => state.clinicianProfile);

  const [selectedField, setSelectedField] = useState<'adult' | 'paed'>('adult');

  const onSubmit = methods.handleSubmit(async (data: any) => {
    if (!selectedTenancy) {
      return;
    }

    const clinicId = data.service ? parseInt(data.service as string) : null;
    if (!clinicId) {
      return;
    }

    const selectedClinic = clinics.find((c) => c.clinic_id === clinicId);

    if (!selectedClinic) {
      return;
    }

    const payload: AddTenancyPatientPayload =
      transformToAddTenancyPatientPayload(
        {
          ...data,
          isPaed: selectedField === 'paed',
          clinics: [selectedClinic],
        },
        selectedTenancy.tenancy_id,
        [selectedClinic],
      );

    await addAndInvitePatient(payload)
      .then(() => {
        resetHaveMoreData(true);
        triggerPatientListUpdate();
      })
      .finally(() => {
        handleClose();
      });
  });

  return (
    <FormProvider {...methods}>
      <form className="d-flex flex-column gap-3" onSubmit={onSubmit}>
        <AddPatientFormInfo fields={features.info_fields} />
        {features.recent_EPOCC_fields && (
          <MostRecentEPOCC
            fields={features.recent_EPOCC_fields}
            selectedField={selectedField}
            onUpdateSelectedField={setSelectedField}
          />
        )}
        <div className="d-flex flex-row justify-content-center">
          <CareMapprButton
            isLoading={loading}
            type="submit"
            label="Add and invite"
            extraClassNames="w-25"
          />
        </div>
      </form>
    </FormProvider>
  );
};

export default AddTenancyPatientForm;
